import { isNil } from 'lodash/fp';
import { isRedirectAllowed, toStringFqn } from 'app/lib/utils';
import { QueryState } from 'app/lib/constants';
import { GET_SPACE_OPERATION, GET_SPACE_REFS_OPERATION } from 'app/store/__generated__/operations';
import { transformSpace } from 'app/store/transformers.js';
import { doValidateSnowflakeAccess } from 'app/store/access/actions';
import { selectNonDeployingUpstreamSourceFqns } from 'app/store/deployView/selectors.js';
import { selectCurrentUserName } from 'app/store/users/selectors.js';
import { navigateToWorkbench, isWorkbench } from 'app/routes';
import { selectEntityIdsBySlug } from 'app/store/entities/selectors.js';
import { doCopyExplorations } from 'app/store/exploreData/actions.js';
import { selectSelectedTableExplorations } from 'app/store/exploreData/selectors.js';
import { doGetTableDetailsForDataBrowser } from 'app/store/tableDetails/actions.js';
import {
    doDeleteDatasetQuery,
    doLookupAndCancelQuery
} from 'app/store/snowflakeQueries/actions.js';
import { selectQueryMetadataForPropEntityId } from 'app/store/snowflakeQueries/selectors.js';
import { UPDATE_PROJECT_ENV } from 'app/lib/types/server';
import { selectProjectErrorNodeUids } from 'app/store/flow/selectors.js';
import {
    selectCurrentProjectId,
    selectSelectedEntityId,
    selectSlugForPropEntityFqn,
    selectProjectUid
} from './selectors.js';
import {
    LOAD_PROJECT,
    LOAD_PROJECTS,
    SET_CURRENT_ENTITY,
    CREATE_PROJECT,
    CLONE_PROJECT,
    LOAD_CURRENT_PROJECT,
    SYNC_PROJECT,
    OPEN_CREATE_PROJECT_DIALOG,
    CLOSE_CREATE_PROJECT_DIALOG,
    LOAD_UPSTREAM_DATASOURCES,
    GET_PROJECT_INPUTS,
    SELECT_TABLE,
    UPDATE_PROJECT_ENV_ACTION_TYPE,
    TOGGLE_CREATE_PROJECT_DIALOG
} from './types.js';

export const doLoadUpstreamDatasources = (
    spaceUid,
    projectErrorNodeUids = [],
    showNotification = false
) => ({
    type: LOAD_UPSTREAM_DATASOURCES,
    api: { version: 2, name: 'GetProjectDependencies' },
    params: { spaceUid },
    meta: (state) => {
        const user = selectCurrentUserName(state);
        return { user };
    },
    onSucceeded: ({ getState, dispatch }) => {
        const state = getState();
        const fqnObjectsList = selectNonDeployingUpstreamSourceFqns(state);

        dispatch(doValidateSnowflakeAccess(fqnObjectsList, projectErrorNodeUids, showNotification));
    }
});

export const doLoadProject = ({
    projectSlug,
    fetchNewSources = true,
    followUps = [],
    isInitialLoad = false
}) => ({
    type: LOAD_PROJECT,
    api: { ...GET_SPACE_OPERATION, sync: true },
    transformer: transformSpace,
    params: { spaceId: projectSlug },
    meta: (state) => {
        const user = selectCurrentUserName(state);
        const projectErrorNodeUids = selectProjectErrorNodeUids(state);

        return { user, projectErrorNodeUids };
    },
    onSucceeded: ({ getState, dispatch, meta }) => {
        if (fetchNewSources) {
            const state = getState();
            const projectId = selectCurrentProjectId(state) ?? projectSlug;
            const showNotification = !isInitialLoad && isWorkbench();
            dispatch(
                doLoadUpstreamDatasources(projectId, meta.projectErrorNodeUids, showNotification)
            );
        }

        followUps.forEach(dispatch);
    }
});

export const doLoadCurrentProject = (fetchNewSources = true) => ({
    type: LOAD_CURRENT_PROJECT,
    effect: ({ store }) => {
        const state = store.getState();
        const projectId = selectCurrentProjectId(state);

        if (projectId) {
            store.dispatch(doLoadProject({ projectSlug: projectId, fetchNewSources }));
        }
    }
});

export const doLoadProjectAndSelectNode = ({ spaceId, nodeFqn, isAddingExplorations }) => ({
    type: LOAD_PROJECT,
    api: { ...GET_SPACE_OPERATION, sync: true },
    transformer: transformSpace,
    params: { spaceId },
    onSucceeded: ({ getState, dispatch }) => {
        const state = getState();
        if (nodeFqn) {
            const { uid, spaceSlug } = selectSlugForPropEntityFqn(state, {
                fqn: nodeFqn,
                spaceUid: spaceId
            });
            if (isAddingExplorations) {
                const viewUuid = selectEntityIdsBySlug(state)[`${spaceSlug}/${uid}`];
                const explorations = selectSelectedTableExplorations(state, { fqn: nodeFqn });

                for (const [, value] of Object.entries(explorations)) {
                    if (value?.model) {
                        dispatch(
                            doCopyExplorations({
                                viewUuid,
                                spaceId,
                                model: value.model
                            })
                        );
                    }
                }
            }

            navigateToWorkbench(spaceSlug, uid);
        }
    }
});

export const doLoadProjects = () => ({
    type: LOAD_PROJECTS,
    api: GET_SPACE_REFS_OPERATION,
    meta: (state) => {
        const user = selectCurrentUserName(state);
        return { user };
    },
    transformer: ({ data }) => data.spaceRefDtos.map((space) => ({ ...space }))
});

export const doSetCurrentEntity = (projectId, entityId) => ({
    type: SET_CURRENT_ENTITY,
    payload: {
        projectId,
        entityId
    },
    immediateEffect: ({ store }) => {
        const state = store.getState();
        const selectedEntityId = selectSelectedEntityId(state);

        if (selectedEntityId) {
            const metadata = selectQueryMetadataForPropEntityId(state, {
                entityId: selectedEntityId
            });

            if (metadata.state === QueryState.RUNNING) {
                store.dispatch(doLookupAndCancelQuery(['byEntityId', selectedEntityId]));
                store.dispatch(doDeleteDatasetQuery({ entityId: selectedEntityId }));
            }
        }
    }
});

export const doOpenCreateProjectDialog = ({ objectToImport, isAddingExplorations } = {}) => ({
    type: OPEN_CREATE_PROJECT_DIALOG,
    params: { objectToImport, isAddingExplorations }
});

export const doCloseCreateProjectDialog = () => ({
    type: CLOSE_CREATE_PROJECT_DIALOG
});

export const doToggleCreateProjectDialog = () => ({
    type: TOGGLE_CREATE_PROJECT_DIALOG
});

export const doCreateProject = ({
    name,
    addressToImport = null,
    isAddingExplorations,
    envUid
}) => ({
    type: CREATE_PROJECT,
    api: { version: 2, name: 'CreateSpaceWithView' },
    params: (state) => {
        const user = selectCurrentUserName(state);
        return { name, addressToImport, user, envUid };
    },
    onSucceeded: ({ payload, meta, dispatch }) => {
        if (isRedirectAllowed(meta.sessionId)) {
            if (!isNil(addressToImport)) {
                dispatch(
                    doLoadProjectAndSelectNode({
                        spaceId: payload.uid,
                        nodeFqn: toStringFqn(addressToImport),
                        isAddingExplorations
                    })
                );
                dispatch(doGetTableDetailsForDataBrowser({ tables: [addressToImport] }));
            } else {
                navigateToWorkbench(payload.slug);
            }
        }
    }
});

export const doCloneProject = ({ from, name }) => ({
    type: CLONE_PROJECT,
    api: { version: 2, name: 'CopySpace' },
    params: { from, name },
    onSucceeded: ({ payload, meta, dispatch }) => {
        if (isRedirectAllowed(meta.sessionId)) {
            navigateToWorkbench(payload.slug);
        }

        dispatch(doLoadProjects());
    }
});

export const doSyncProject = (outOfSyncTables) => ({
    type: SYNC_PROJECT,
    api: { version: 2, name: 'SyncProject' },
    params: (state) => {
        const spaceId = selectCurrentProjectId(state);
        return { spaceId, outOfSyncTables };
    },
    onSucceeded: ({ dispatch }) => {
        dispatch(doLoadCurrentProject());
    }
});

export const doGetProjectInputs = (projectUid) => ({
    type: GET_PROJECT_INPUTS,
    api: { version: 2, name: 'GetProjectInputs' },
    params: { spaceUid: projectUid }
});

export const doSelectTable = ({ fqn }) => ({
    type: SELECT_TABLE,
    payload: { fqn }
});

export const doUpdateProjectEnv = (envUid, projectUidProp) => ({
    type: UPDATE_PROJECT_ENV_ACTION_TYPE,
    api: { version: 2, name: UPDATE_PROJECT_ENV },
    params: (state) => {
        const projectUid = projectUidProp ?? selectProjectUid(state);

        return {
            envUid,
            spaceUid: projectUid
        };
    },
    onSucceeded: ({ dispatch }) => {
        dispatch(doLoadCurrentProject());
    }
});
